export type WidgetAccessGroup =
  | "default-widgets"
  | "equities-suite-widgets"
  | "squawk-metals"
  | "squawk-multi-asset"
  | "squawk-fixed-income"
  | "squawk-energy"
  | "squawk-forex"
  | "squawk-equities";

/**
 * Filter features a widget can support.
 * Controls which sections appear in the filter/highlight library modal.
 */
export type WidgetFilterFeature =
  | "ticker-watchlists"
  | "keyword-watchlists"
  | "tag-watchlists"
  | "asset-classes"
  | "advanced-filters"
  | "sources";

export type Widget = {
  id: string;
  name: string;
  component: string;
  /**
   * Picker-only label shown in the Manage Widgets panel. Falls back to `name`.
   * NEVER used for the on-dashboard widget header (that always uses `name`).
   */
  displayName?: string;
  /** One-line functional description shown under the name in the picker. */
  description?: string;
  /** Subgroup label within a top-level group (e.g. "SEC", "Broker"). */
  subgroup?: string;
  /** Extra search terms — words dropped from displayName but still searchable. */
  keywords?: string[];
  /** Widget is only visible to internal Newsquawk users (hidden from external users entirely) */
  internalOnly?: boolean;
  /**
   * Early-access widget: visible only to internal users and members of the
   * `beta-widgets-preview-group` (which carries the `beta-widgets` client role).
   * Orthogonal to `accessGroup` — grants access regardless of subscription tier.
   */
  betaPreview?: boolean;
  /** Widget renders its full content without authentication (e.g. TradingView charts) */
  unAuthAccessible?: boolean;
  /** Widget shows demo content (24h delayed) when not authenticated */
  demoAccessible?: boolean;
  /** Widget is hidden from the manage widget menu when not logged in */
  hideFromMenuWhenLoggedOut?: boolean;
  /** Role required to access this widget. If unset, widget is accessible to all authenticated users. */
  accessGroup?: WidgetAccessGroup;
  /** Filter features this widget supports. If unset, widget has no filter panel. */
  filterFeatures?: WidgetFilterFeature[];
};

export type WidgetGroup = {
  [key: string]: Widget[];
};

/**
 * Top-level groups mirror how Newsquawk markets the product (newsquawk.com):
 * Human Intelligence / Curated External Intelligence / Equities Intelligence Suite,
 * plus third-party External Widgets, a hidden Beta Preview group (opt-in via the
 * `beta-widgets` role), and a hidden Internal Only group.
 *
 * Array order IS display order (curated). Items sharing a `subgroup` are kept
 * contiguous so the picker can render a subgroup divider on the boundary.
 */
export const widgetGroups: WidgetGroup = {
  "Human Intelligence": [
    {
      id: "NewsquawkHeadlines",
      name: "Newsquawk Headlines",
      component: "NewsquawkHeadlines",
      displayName: "Headlines",
      description: "Real-time editorial headline feed",
      keywords: ["Newsquawk"],
      unAuthAccessible: true,
      accessGroup: "default-widgets",
      filterFeatures: ["ticker-watchlists", "keyword-watchlists", "tag-watchlists", "asset-classes", "advanced-filters"],
    },
    {
      id: "ResearchHeadlines",
      name: "Newsquawk Research Reports",
      component: "ResearchHeadlines",
      displayName: "Research Reports",
      description: "Analyst research notes & reports",
      keywords: ["Newsquawk", "research"],
      accessGroup: "default-widgets",
      filterFeatures: ["ticker-watchlists", "keyword-watchlists", "tag-watchlists", "asset-classes", "advanced-filters"],
    },
    {
      id: "EconomicCalendar",
      // Renders the previous day's events for logged-out visitors — the
      // calendar_events endpoint is public and the widget passes isDemo.
      demoAccessible: true,
      name: "Newsquawk Economic Calendar",
      component: "EconomicCalendar",
      displayName: "Economic Calendar",
      description: "Scheduled releases & live results, incl. exclusive Newsquawk events",
      keywords: ["Newsquawk"],
      accessGroup: "default-widgets",
    },
    {
      id: "UsMacroData",
      name: "Newsquawk US Macro Data",
      component: "UsMacroData",
      displayName: "US Macro Data",
      description: "Key US macroeconomic indicators",
      keywords: ["Newsquawk", "macro"],
    },
    {
      id: "MarketSnapshot",
      name: "Newsquawk Market Snapshot",
      component: "MarketSnapshot",
      displayName: "Market Snapshot",
      description: "Cross-asset market overview",
      keywords: ["Newsquawk"],
    },
    {
      id: "FundingDashboard",
      name: "Newsquawk Funding Dashboard",
      component: "FundingDashboard",
      displayName: "Funding Dashboard",
      description: "Funding & rates dashboard",
      keywords: ["Newsquawk"],
    },
  ],
  "Curated External Intelligence": [
    {
      id: "FirehoseAll",
      name: "External Firehose",
      component: "FirehoseAll",
      displayName: "External Firehose",
      description: "Every accessible 3rd-party news source, combined",
      demoAccessible: true,
      accessGroup: "default-widgets",
      filterFeatures: ["ticker-watchlists", "keyword-watchlists", "advanced-filters", "sources"],
    },
    {
      id: "CryptoWeb",
      name: "Crypto Blogs & Websites",
      component: "CryptoWeb",
      displayName: "Crypto Blogs & Websites",
      description: "Crypto blog & website coverage",
      demoAccessible: true,
      accessGroup: "default-widgets",
      filterFeatures: ["ticker-watchlists", "keyword-watchlists", "advanced-filters"],
    },
    {
      id: "EnergyWeb",
      name: "Energy Blogs & Websites",
      component: "EnergyWeb",
      displayName: "Energy Blogs & Websites",
      description: "Energy-sector blog & website coverage",
      demoAccessible: true,
      accessGroup: "default-widgets",
      filterFeatures: ["ticker-watchlists", "keyword-watchlists", "advanced-filters"],
    },
    {
      id: "EnergyX",
      name: "Energy X (Twitter)",
      component: "EnergyX",
      displayName: "Energy X",
      description: "Energy-sector posts from X (Twitter)",
      keywords: ["Twitter"],
      demoAccessible: true,
      accessGroup: "default-widgets",
      filterFeatures: ["ticker-watchlists", "keyword-watchlists", "advanced-filters"],
    },
    {
      id: "FinX",
      name: "Fin X (Twitter)",
      component: "FinX",
      displayName: "Fin X",
      description: "Financial posts from X (Twitter)",
      keywords: ["Twitter"],
      demoAccessible: true,
      accessGroup: "default-widgets",
      filterFeatures: ["ticker-watchlists", "keyword-watchlists", "advanced-filters"],
    },
    {
      id: "TruthSocial",
      name: "Truth Social",
      component: "TruthSocial",
      displayName: "Truth Social",
      description: "President Trump's Truth Social posts",
      keywords: ["Trump"],
      demoAccessible: true,
      accessGroup: "default-widgets",
      filterFeatures: ["ticker-watchlists", "keyword-watchlists", "advanced-filters"],
    },
  ],
  "Equities Intelligence Suite": [
    {
      id: "StocksNews",
      name: "Equities News",
      component: "StocksNews",
      displayName: "Equities News",
      description: "Equities news from official partners — The Fly, Benzinga & Trade Xchange",
      demoAccessible: true,
      accessGroup: "equities-suite-widgets",
      filterFeatures: ["ticker-watchlists", "keyword-watchlists", "advanced-filters"],
    },
    {
      id: "StocksRatings",
      name: "Broker Ratings",
      component: "StocksRatings",
      displayName: "Broker Ratings",
      description: "Analyst upgrades, downgrades & price targets",
      accessGroup: "equities-suite-widgets",
      filterFeatures: ["ticker-watchlists", "keyword-watchlists", "advanced-filters"],
    },
    {
      id: "USTraderNews",
      name: "Trade Xchange",
      component: "USTraderNews",
      displayName: "Trade Xchange",
      description: "Trade Xchange partner feed, standalone — US trader news & filings",
      accessGroup: "equities-suite-widgets",
      filterFeatures: ["ticker-watchlists", "keyword-watchlists", "advanced-filters"],
    },
    {
      id: "OptionActivityFeed",
      name: "Option Activity Feed",
      component: "OptionActivityFeed",
      displayName: "Option Activity",
      description: "Unusual options sweeps & blocks with scoring",
      keywords: ["options"],
      accessGroup: "equities-suite-widgets",
      filterFeatures: ["ticker-watchlists", "keyword-watchlists"],
    },
    {
      id: "InsiderTransactionFeed",
      name: "Insider Transaction Feed",
      component: "InsiderTransactionFeed",
      displayName: "Insider Transactions",
      description: "SEC Form 4/5 insider buys, sells & awards",
      keywords: ["SEC"],
      accessGroup: "equities-suite-widgets",
      filterFeatures: ["ticker-watchlists", "keyword-watchlists"],
    },
    {
      id: "GovernmentTradeFeed",
      name: "Government Trade Feed",
      component: "GovernmentTradeFeed",
      displayName: "Government Trades",
      description: "US congressional trades (STOCK Act)",
      accessGroup: "equities-suite-widgets",
      filterFeatures: ["ticker-watchlists", "keyword-watchlists"],
    },
    {
      id: "BlockTradeFeed",
      name: "Block Trade Feed",
      component: "BlockTradeFeed",
      displayName: "Block Trades",
      description: "Large institutional equity block trades",
      accessGroup: "equities-suite-widgets",
      filterFeatures: ["ticker-watchlists", "keyword-watchlists"],
    },
    {
      id: "SecFilings",
      name: "Newsquawk SEC Filings",
      component: "SecFilings",
      displayName: "Filings",
      description: "SEC filings table with AI change summaries",
      subgroup: "SEC",
      keywords: ["SEC", "Newsquawk", "13F"],
      accessGroup: "equities-suite-widgets",
    },
    {
      id: "SecFilingStories",
      name: "Newsquawk SEC Filing Stories",
      component: "SecFilingStories",
      displayName: "Filing Stories",
      description: "Write-ups of notable SEC filings",
      subgroup: "SEC",
      keywords: ["SEC", "Newsquawk"],
      accessGroup: "equities-suite-widgets",
    },
    {
      id: "SecFilingActivity",
      name: "Newsquawk SEC Filing Activity",
      component: "SecFilingActivity",
      displayName: "Filing Activity",
      description: "Aggregate SEC filing volume & trends",
      subgroup: "SEC",
      keywords: ["SEC", "Newsquawk"],
      accessGroup: "equities-suite-widgets",
    },
    {
      id: "SecStockFlow",
      name: "Newsquawk SEC Stock Flow",
      component: "SecStockFlow",
      displayName: "Stock Flow",
      description: "Institutional flow derived from 13F data",
      subgroup: "SEC",
      keywords: ["SEC", "Newsquawk", "13F"],
      accessGroup: "equities-suite-widgets",
    },
    {
      id: "Sec13FStocksFeed",
      name: "SEC 13F Stocks Feed",
      component: "Sec13FStocksFeed",
      displayName: "13F Stocks Feed",
      description: "Per-stock 13F holdings changes (new/inc/closed)",
      subgroup: "SEC",
      keywords: ["SEC", "13F"],
      accessGroup: "equities-suite-widgets",
      filterFeatures: ["ticker-watchlists", "keyword-watchlists"],
    },
  ],
  "External Widgets": [
    // No descriptions in this pillar — the brand IS the spec. The group tagline
    // ("Third-party charts, calendars & live business news") covers the category.
    {
      id: "TvAdvancedRealTimeChart",
      name: "Trading View Real Time Chart",
      component: "TvAdvancedRealTimeChart",
      displayName: "TradingView Real Time Chart",
      keywords: ["chart"],
      unAuthAccessible: true,
      accessGroup: "default-widgets",
    },
    {
      id: "TvActiveStocks",
      name: "Trading View Active Stocks",
      component: "TvActiveStocks",
      displayName: "TradingView Active Stocks",
      unAuthAccessible: true,
      accessGroup: "default-widgets",
    },
    {
      id: "TvMarketData",
      name: "Trading View Market Data",
      component: "TvMarketData",
      displayName: "TradingView Market Data",
      unAuthAccessible: true,
      accessGroup: "default-widgets",
    },
    {
      id: "BloombergLive",
      name: "Bloomberg Business News Live",
      component: "BloombergLive",
      displayName: "Bloomberg Business News Live",
      unAuthAccessible: true,
      hideFromMenuWhenLoggedOut: true,
      accessGroup: "default-widgets",
    },
    {
      id: "InvEconCalendar",
      name: "Investing.com Calendar",
      component: "InvEconCalendar",
      displayName: "Investing.com Calendar",
      unAuthAccessible: true,
      accessGroup: "default-widgets",
    },
  ],
  "Beta Preview": [
    {
      id: "NlpSearch",
      name: "Newsquawk Ask",
      component: "NlpSearch",
      displayName: "Ask",
      description: "Search Newsquawk headlines in plain English — \"the 10 most recent Tesla stories\"",
      keywords: ["search", "ask", "natural language", "AI"],
      // Same pattern as the calendar below: betaPreview short-circuits before
      // accessGroup, so this is inert today and graduation lands it on the
      // subscriber tier rather than opening it to everyone.
      accessGroup: "default-widgets",
      betaPreview: true,
    },
    {
      id: "EconomicCalendarV2",
      name: "Newsquawk Economic Calendar v2",
      component: "EconomicCalendarV2",
      displayName: "Economic Calendar v2",
      description: "Next-gen calendar backend (data, earnings, supply, speakers)",
      keywords: ["Newsquawk", "calendar"],
      // `betaPreview` short-circuits before `accessGroup` in hasAccessToWidget, so
      // this is inert today — kept so that dropping `betaPreview` on graduation
      // lands it back on the subscriber tier rather than opening it to everyone.
      accessGroup: "default-widgets",
      betaPreview: true,
    },
    {
      id: "BrokerPriceLadderSP",
      name: "Price Ladder - S&P (Dummy Data)",
      component: "BrokerPriceLadderSP",
      displayName: "Price Ladder S&P (Dummy Data)",
      description: "S&P futures price ladder — mockup, randomised prices",
      subgroup: "Broker",
      betaPreview: true,
    },
    {
      id: "BrokerPriceLadderNQ",
      name: "Price Ladder - NQ (Dummy Data)",
      component: "BrokerPriceLadderNQ",
      displayName: "Price Ladder NQ (Dummy Data)",
      description: "Nasdaq futures price ladder — mockup, randomised prices",
      subgroup: "Broker",
      betaPreview: true,
    },
    {
      id: "BrokerPriceLadderYM",
      name: "Price Ladder - DOW (Dummy Data)",
      component: "BrokerPriceLadderYM",
      displayName: "Price Ladder DOW (Dummy Data)",
      description: "Dow futures price ladder — mockup, randomised prices",
      subgroup: "Broker",
      betaPreview: true,
    },
    {
      id: "BrokerTicket",
      name: "Broker Ticket",
      component: "BrokerTicket",
      displayName: "Broker Ticket",
      description: "Trade via your own MetaTrader broker — market orders with SL/TP",
      keywords: ["broker", "trade", "metatrader", "order"],
      subgroup: "Broker",
      betaPreview: true,
    },
    {
      id: "BrokerPositions",
      name: "Broker Positions",
      component: "BrokerPositions",
      displayName: "Broker Positions",
      description: "Open positions at your MetaTrader broker, with one-click close",
      keywords: ["broker", "trade", "metatrader", "blotter"],
      subgroup: "Broker",
      betaPreview: true,
    },
    {
      id: "BrokerLadder",
      name: "Broker Ladder",
      component: "BrokerLadder",
      displayName: "Broker Ladder",
      description: "Click-trading price ladder — work limits/stops at price levels",
      keywords: ["broker", "trade", "metatrader", "ladder", "dom", "depth"],
      subgroup: "Broker",
      betaPreview: true,
    },
  ],
  "Internal Only": [
    {
      id: "GeneralWeb",
      name: "General News Web Scraper",
      component: "GeneralWeb",
      displayName: "General Web",
      description: "General financial news web feed",
      subgroup: "Tools & Feeds",
      keywords: ["scraper"],
      internalOnly: true,
      filterFeatures: ["ticker-watchlists", "keyword-watchlists", "advanced-filters"],
    },
    {
      id: "FinancialReportsEU",
      name: "Financial Reports EU",
      component: "FinancialReportsEU",
      displayName: "Financial Reports EU",
      description: "EU corporate financial reports feed",
      subgroup: "Tools & Feeds",
      internalOnly: true,
      filterFeatures: ["ticker-watchlists", "keyword-watchlists", "advanced-filters"],
    },
    {
      id: "CreateHeadline",
      name: "Create Headline",
      component: "CreateHeadline",
      displayName: "Create Headline",
      description: "Internal headline authoring tool",
      subgroup: "Tools & Feeds",
      internalOnly: true,
    },
    {
      id: "MarketSnapshotAdmin",
      name: "Market Snapshot Admin",
      component: "MarketSnapshotAdmin",
      displayName: "Market Snapshot Admin",
      description: "Market Snapshot admin controls",
      subgroup: "Tools & Feeds",
      accessGroup: "equities-suite-widgets",
      internalOnly: true,
    },
    {
      id: "TestComponent",
      name: "Test Component",
      component: "TestComponent",
      displayName: "Test Component",
      description: "Internal test component",
      subgroup: "Tools & Feeds",
      internalOnly: true,
    },
  ],
};

/** Marketing tagline shown as a muted sub-header beneath each group title. */
export const widgetGroupTaglines: Record<string, string> = {
  "Human Intelligence": "Real-time analysis & research from the Newsquawk desk",
  "Curated External Intelligence":
    "Algorithmic discovery across social, blogs & alt sources",
  "Equities Intelligence Suite":
    "SEC filings, options flow, insider activity & ratings",
  "External Widgets": "Third-party charts, calendars & live business news",
  "Beta Preview":
    "Early access — in development, subject to change without notice",
  "Internal Only": "Newsquawk staff tools — not visible to subscribers",
};

export const internalOnlyWidgetGroups = ["Internal Only"];

export const betaPreviewWidgetGroups = ["Beta Preview"];

export const defaultWidgetSizes = {
  // External widgets
  TvActiveStocks: { w: 5, h: 4, x: 0, y: 0, minW: 3 },
  TvAdvancedRealTimeChart: { w: 9, h: 5, x: 0, y: 4, minW: 4 },
  InvEconCalendar: { w: 4, h: 4, x: 0, y: 0, minW: 3 },
  BloombergLive: { w: 4, h: 3, x: 0, y: 0, minW: 3 },
  TvMarketData: { w: 5, h: 4, x: 0, y: 0, minW: 3 },

  // Broker widgets
  BrokerPriceLadderSP: { w: 3, h: 7, x: 0, y: 0, minW: 2 },
  BrokerPriceLadderNQ: { w: 3, h: 7, x: 0, y: 0, minW: 2 },
  BrokerPriceLadderYM: { w: 3, h: 7, x: 0, y: 0, minW: 2 },
  BrokerTicket: { w: 4, h: 4, x: 0, y: 0, minW: 3 },
  BrokerPositions: { w: 6, h: 4, x: 0, y: 0, minW: 4 },
  BrokerLadder: { w: 4, h: 8, x: 0, y: 0, minW: 3 },

  // Twitter/social widgets

  // Internal tools
  TestComponent: { w: 4, h: 4, x: 0, y: 0, minW: 2 },
  CreateHeadline: { w: 4, h: 4, x: 0, y: 0, minW: 3 },

  // News headlines - list widgets
  NewsquawkHeadlines: { w: 5, h: 4, x: 0, y: 0, minW: 3 },
  ResearchHeadlines: { w: 4, h: 4, x: 0, y: 0, minW: 3 },

  // Table widgets
  SecFilings: { w: 8, h: 6, x: 0, y: 0, minW: 5 },
  SecFilingStories: { w: 5, h: 6, x: 0, y: 0, minW: 4 },
  SecFilingActivity: { w: 5, h: 6, x: 0, y: 0, minW: 4 },
  SecStockFlow: { w: 5, h: 5, x: 0, y: 0, minW: 3 },

  // Firehose Widgets
  FirehoseAll: { w: 4, h: 5, x: 0, y: 0, minW: 3 },
  CryptoWeb: { w: 4, h: 5, x: 0, y: 0, minW: 3 },
  EnergyWeb: { w: 4, h: 5, x: 0, y: 0, minW: 3 },
  EnergyX: { w: 4, h: 5, x: 0, y: 0, minW: 3 },
  TruthSocial: { w: 4, h: 5, x: 0, y: 0, minW: 3 },
  StocksNews: { w: 4, h: 5, x: 0, y: 0, minW: 3 },
  FinX: { w: 4, h: 5, x: 0, y: 0, minW: 3 },
  GeneralWeb: { w: 4, h: 5, x: 0, y: 0, minW: 3 },
  FinancialReportsEU: { w: 4, h: 5, x: 0, y: 0, minW: 3 },
  EconomicCalendar: { w: 4, h: 5, x: 0, y: 0, minW: 3 },
  NlpSearch: { w: 5, h: 6, x: 0, y: 0, minW: 3 },
  EconomicCalendarV2: { w: 4, h: 5, x: 0, y: 0, minW: 3 },
  StocksRatings: { w: 4, h: 5, x: 0, y: 0, minW: 3 },
  USTraderNews: { w: 4, h: 5, x: 0, y: 0, minW: 3 },

  // Equities widgets
  InsiderTransactionFeed: { w: 8, h: 5, x: 0, y: 0, minW: 5 },
  GovernmentTradeFeed: { w: 8, h: 5, x: 0, y: 0, minW: 5 },
  OptionActivityFeed: { w: 8, h: 5, x: 0, y: 0, minW: 5 },
  BlockTradeFeed: { w: 8, h: 5, x: 0, y: 0, minW: 5 },
  Sec13FStocksFeed: { w: 8, h: 5, x: 0, y: 0, minW: 5 },
  // Henry widgets
  FundingDashboard: { w: 8, h: 6, x: 0, y: 0, minW: 5 },
  UsMacroData: { w: 10, h: 6, x: 0, y: 0, minW: 6 },
  MarketSnapshot: { w: 8, h: 5, x: 0, y: 0, minW: 5 },
  MarketSnapshotAdmin: { w: 10, h: 6, x: 0, y: 0, minW: 6 },
};

export const allWidgets = Object.values(widgetGroups).flat();

const validWidgetIds = new Set(allWidgets.map((w) => w.id));

/**
 * Extracts the base widget ID from either a composite ID ("NewsquawkHeadlines-abc123")
 * or a bare ID ("NewsquawkHeadlines"). Checks against the widget registry to find the
 * longest matching prefix, falling back to splitting on the first "-".
 *
 * Prefer this over `startsWith` when matching a composite id to a widget type:
 * a prefix test conflates ids where one is a prefix of another, e.g.
 * "EconomicCalendarV2-x" matches "EconomicCalendar" and "MarketSnapshotAdmin-x"
 * matches "MarketSnapshot".
 */
export function extractBaseId(id: string): string {
  // Check if the full id is itself a valid widget base ID (bare ID)
  if (validWidgetIds.has(id)) return id;
  // Otherwise split off the unique suffix
  const dashIdx = id.indexOf("-");
  return dashIdx === -1 ? id : id.substring(0, dashIdx);
}

const getWidgetGroup = (widgetId: string) => {
  const group = Object.keys(widgetGroups).find((group) =>
    widgetGroups[group as keyof typeof widgetGroups].some(
      (widget) => widget.id === widgetId
    )
  );
  return group;
};

export const getWidgetById = (widgetId: string) => {
  return allWidgets.find((widget) => widget.id === widgetId);
};

export const isInternalOnlyWidget = (widgetId: string) => {
  return (
    (allWidgets.find((widget) => widget.id === widgetId)?.internalOnly ??
      false) ||
    internalOnlyWidgetGroups.includes(getWidgetGroup(widgetId) ?? "")
  );
};

export const isBetaPreviewWidget = (widgetId: string) => {
  return (
    (allWidgets.find((widget) => widget.id === widgetId)?.betaPreview ??
      false) ||
    betaPreviewWidgetGroups.includes(getWidgetGroup(widgetId) ?? "")
  );
};
